import { api } from "../../lib/api";
import { IMAGE_TYPES, PREVWORK_IMAGE_TYPES, MULTI_TYPES } from "./model";
import type { ListingPurchaseOfferModel } from "./useListingPurchaseOffer";

export function createPostFlowPublication(model: ListingPurchaseOfferModel) {
  const {
    type, current, map, user, setUser, navigate, returnTo, step, setStep, form,
    rentalRatePreviewOnly, canPostRent, selectedItemType, requiresItemCondition,
    productInformationError, busy, setBusy, setError, setDoneTab, setPublishedPostId,
    deadlineInputValid, setFailedProjectDocuments, setPublishedProjectId, images, prevImages,
    extraTitles, setBatchCreated, batchRef, uploadPhotos, paywall,
    setPaywall, setPlanLimit, enablePrompt, setEnablePrompt, setEnableError, selectedResumeId,
    initialActiveResumeId, newResumeIds, setNewResumeIds, resumeFileBusy, set, rfqDetailsError,
    rfqDesiredTermsError, projectFiles, draft, buildPayload, reviewError
  } = model;
  async function uploadSelectedPhotos(uploadType: string, listingId: string) {
    const photos = images.map((file, index) => ({ uploadType, listingId, file, label: `Photo ${index + 1}` }));
    if (PREVWORK_IMAGE_TYPES.includes(type)) photos.push(...prevImages.map((file, index) => ({ uploadType: `${uploadType}-prev`, listingId, file, label: `Previous work ${index + 1}` })));
    await uploadPhotos(photos);
  }
  // Category exactly as buildPayload sends it — used for the paywall quota check.
  const postCategory = () => (form.category.trim() || "General");
  // v3.9.38 — the set of titles to publish: any queued extra titles + the current
  // title, de-duplicated. Single-title types collapse to just the one.
  function allTitles(): string[] {
    const list = MULTI_TYPES.includes(type) ? extraTitles.map((t) => t.trim()).filter(Boolean) : [];
    const cur = form.title.trim();
    if (cur) list.push(cur);
    return Array.from(new Set(list.map((t) => t))); // preserve order, drop dupes
  }
  // Create one post with the shared form details; return its ID for the success link.
  async function createOne(title: string): Promise<string | null> {
    if (type === "item") {
      const itemRes = await api.createItem(model.purchaseOffer.publicationPayload(title));
      const id = itemRes?.item?.id;
      if (typeof id !== "string" || !id) return null;
      draft.clear();
      await uploadSelectedPhotos("item", id);
      return id;
    }
    const res = await api.createListing(map!.endpoint, { ...buildPayload(), title });
    const id = res?.item?.id;
    if (typeof id !== "string" || !id) return null;
    draft.clear();
    if (IMAGE_TYPES.includes(type) || type === "rfq") await uploadSelectedPhotos(map!.endpoint, id);
    if (type === "project") {
      setPublishedProjectId(id);
      const failed: string[] = [];
      for (const file of projectFiles) {
        try { await api.uploadAttachment(id, file); }
        catch { failed.push(file.name); }
      }
      setFailedProjectDocuments(failed);
    }
    return id;
  }
  // Publish the queued titles one by one. Pauses on the posting paywall (per post)
  // and resumes from the same index after payment via the modal's onEntitled.
  async function runBatch() {
    setBusy(true); setError(null);
    const cat = postCategory();
    const b = batchRef.current;
    try {
      // [#28] The capability is enabled explicitly (with confirmation) in publish()
      // BEFORE we get here — no silent auto-grant.
      while (b.idx < b.titles.length) {
        // v3.9.34 paywall — no-op unless enabled AND over the free limit for this post.
        const access = await api.billingStatus(type, cat).catch(() => null);
        if (access && access.enabled && access.allowed === false) {
          setBusy(false); setPaywall({ fees: access.fees, category: cat }); return; // resume via onEntitled
        }
        const id = await createOne(b.titles[b.idx]);
        if (!id) { setError(`Could not publish "${b.titles[b.idx]}". Please try again.`); setBusy(false); return; }
        // A multi-post batch links to its most recently published post.
        setPublishedPostId(id);
        b.created += 1; b.idx += 1;
      }
      setBatchCreated(b.created); setDoneTab(map!.tab); setStep(6); setBusy(false);
    } catch (e: any) {
      model.purchaseOffer.failed(e);
      if (e?.code === "needs_payment") {
        const access = await api.billingStatus(type, cat).catch(() => null);
        setBusy(false);
        setPaywall({ fees: access?.fees || { perPost: 0, subscription: 0, subscriptionDays: 30 }, category: cat });
        return;
      }
      if (e?.code === "plan_limit") { setPlanLimit(e?.message || "You've reached your plan's limit."); setBusy(false); return; }
      if (type === "job" && e?.code === "invalid_job_details") setStep(2);
      setError(e?.message || "Publish failed."); setBusy(false);
    }
  }
  async function publish() {
    setError(null);
    // [posting.validation-and-review](../../../../docs/product/marketplace.md#posting-validation-and-review): edits on Review must still satisfy the earlier step gates before
    // any capability or publication write, without navigating away from Review.
    if (reviewError) { setError(reviewError); return; }
    if (!model.purchaseOffer.canPublish) { setError("Review and acknowledge the complete seller offer before publishing."); return; }
    if (type === "item" && form.mode === "rent" && !canPostRent) { setError("Marketplace rentals are temporarily unavailable. Choose For Sale."); setStep(2); return; }
    // [posting.validation-and-review](../../../../docs/product/marketplace.md#posting-validation-and-review): rental rate controls are a UI draft; the current item API and checkout still bill historical rentals per day.
    if (rentalRatePreviewOnly) { setError("Weekly and monthly rates are preview-only. Choose Day in Pricing / Terms to publish."); return; }
    if (!user) { navigate("/login"); return; }
    if (!map) { setDoneTab("soon"); setStep(6); return; }
    if (type === "resume" && resumeFileBusy) {
      setError("Wait for the résumé upload to finish before publishing.");
      return;
    }
    const titles = allTitles();
    if (type === "job" && !deadlineInputValid) { setError("Enter a valid deadline (MM/DD/YYYY), or clear the field."); setStep(4); return; }
    if (titles.length === 0) { setError("A title is required."); setStep(2); return; }
    if (type === "job" && !form.workArrangement) { setError("Choose whether this job is on-site, hybrid, or remote."); setStep(2); return; }
    if (type === "rfq") {
      const detailsError = rfqDetailsError();
      if (detailsError) { setError(detailsError); setStep(2); return; }
      const termsError = rfqDesiredTermsError();
      if (termsError) { setError(termsError); setStep(4); return; }
    }
    if (type === "item" && (!selectedItemType || !selectedItemType.modes.some((mode) => mode === form.mode))) { setError("Select the item type."); setStep(2); return; }
    if (type === "item" && requiresItemCondition && !form.condition) { setError("Select the item condition."); setStep(2); return; }
    if (type === "item" && selectedItemType?.conditionProfile === "business" && !form.businessOperatingStatus) { setError("Select the business operating status."); setStep(2); return; }
    if (productInformationError) { setError(productInformationError); setStep(2); return; }
    if (type === "item" && !form.mode) { setError("Choose For Sale or For Rent."); setStep(2); return; }
    const proceed = () => { batchRef.current = { titles, idx: 0, created: 0 }; void runBatch(); };
    // [posting.publication-access](../../../../docs/product/marketplace.md#posting-publication-access):
    // adding the required account activity needs explicit confirmation before publication.
    if (map && !user.capabilities?.includes(map.cap)) { setEnableError(null); setEnablePrompt({ cap: map.cap, proceed }); return; }
    proceed();
  }
  async function confirmEnableRole() {
    if (!enablePrompt || busy) return;
    const { cap, proceed } = enablePrompt;
    setBusy(true); setError(null); setEnableError(null);
    try {
      const updatedUser = await api.addActivity(cap);
      if (!updatedUser) throw new Error("Activity update did not return an account.");
      setUser(updatedUser);
    }
    catch { setEnableError("Couldn't enable that activity right now. Please try again."); setBusy(false); return; }
    setBusy(false); setEnablePrompt(null);
    proceed();
  }
  async function discardPost() {
    if (!window.confirm("Are you sure you want to discard this post? Your entered information will be cleared.")) return;
    if (type !== "resume") { draft.clear(); navigate(returnTo); return; }
    setBusy(true); setError(null);
    try {
      for (const id of newResumeIds) {
        await api.deleteResume(id);
        setNewResumeIds((ids) => ids.filter((currentId) => currentId !== id));
      }
      if (initialActiveResumeId && selectedResumeId !== initialActiveResumeId) await api.setActiveResume(initialActiveResumeId);
      setNewResumeIds([]);
      navigate(returnTo);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Could not clear the uploaded résumé. Please try again.";
      setError(message);
    } finally {
      setBusy(false);
    }
  }
  return {
    ...model, postCategory, allTitles, createOne, runBatch,
    publish, confirmEnableRole, discardPost
  };
}
export type PostFlowPublicationModel = ReturnType<typeof createPostFlowPublication>;
